/**
 * Visibility and loading state for the inline suggestion sheet.
 *
 * The sheet is a surface, not a per-response popup: once it has opened it stays
 * open while the input is focused, and a query that is still loading shows a
 * skeleton in place of the rows. Two things load into it — a verb-starter
 * populate (optimistic, shown before the request goes out) and ordinary typing
 * (shown while the request is in flight) — so the two skeleton paths are
 * resolved here and the component is left to render one answer.
 */

import { cn } from '@/lib/cn';
import * as m from 'motion/react-m';
import { useCallback, useEffect, useRef, useState } from 'react';

// Minimum display time: once shown, skeleton stays visible at least this long before swapping to real content.
// Prevents an abrupt flash if API responds almost immediately after the skeleton appears.
const SKELETON_MIN_DISPLAY_MS = 500;

/** Widths cycled across skeleton rows to mimic natural suggestion text lengths */
const SKELETON_ROW_WIDTHS = ['72%', '58%', '80%', '65%'];

/** Row count used before the sheet has ever rendered real suggestions to measure against */
const DEFAULT_SKELETON_ROWS = SKELETON_ROW_WIDTHS.length;

type SkeletonPhase = 'idle' | 'skeleton';

/**
 * Manages the skeleton state for the populate path only (optimistic UI).
 *
 * State machine:
 *   idle → skeleton (forceSearchText set — shown immediately, no grace period)
 *   skeleton → idle (API responds + min display elapsed OR user types)
 *
 * It shows instantly, so the sheet appears the moment a quick option is clicked
 * rather than after the round trip.
 */
function usePopulateSkeletonState(
  forceSearchText: string | null | undefined,
  isSuggestionsPending: boolean,
  searchText: string
): boolean {
  const [phase, setPhase] = useState<SkeletonPhase>('idle');
  const populateTextRef = useRef<string>('');
  const skeletonStartRef = useRef<number>(0);
  const minDisplayTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const resetToIdle = useCallback(() => {
    clearTimeout(minDisplayTimerRef.current);
    populateTextRef.current = '';
    setPhase('idle');
  }, []);

  // Populate action detected — show skeleton immediately (optimistic)
  useEffect(() => {
    if (forceSearchText) {
      populateTextRef.current = forceSearchText;
      clearTimeout(minDisplayTimerRef.current);
      setPhase('skeleton');
      skeletonStartRef.current = Date.now();
    }
  }, [forceSearchText]);

  // Results arrived — respect minimum display time then dismiss
  useEffect(() => {
    if (phase !== 'skeleton' || isSuggestionsPending) return;

    const elapsed = Date.now() - skeletonStartRef.current;
    const remaining = SKELETON_MIN_DISPLAY_MS - elapsed;

    if (remaining <= 0) {
      resetToIdle();
    } else {
      minDisplayTimerRef.current = setTimeout(resetToIdle, remaining);
    }
  }, [isSuggestionsPending, phase, resetToIdle]);

  // User typed — text diverged from populated text, cancel skeleton.
  // Compare trimmed values because searchText is already trimmed (editor.getText().trim())
  // while the populate starter often has a trailing space (e.g. "What is the dosing of ").
  useEffect(() => {
    if (phase === 'idle') return;
    const populateTrimmed = populateTextRef.current.trim();
    if (populateTrimmed && searchText !== populateTrimmed) {
      resetToIdle();
    }
  }, [searchText, phase, resetToIdle]);

  // Cleanup timers on unmount
  useEffect(() => () => clearTimeout(minDisplayTimerRef.current), []);

  return phase === 'skeleton';
}

/**
 * Keeps the sheet mounted while its contents are being replaced.
 *
 * Typing another character must not close a sheet that is already open. Because
 * suggestions come from a mutation whose `data` is cleared as soon as the next
 * request starts, the "still open" signal cannot be the suggestion list itself —
 * it is the list having been non-empty at least once, held across each refresh.
 *
 * The latch drops as soon as a refresh ends with nothing to show (no results, a
 * failed request, an input cleared or grown past the length cap), so this never
 * leaves an empty sheet stranded on screen. It also stays down until the first
 * real results arrive, so the opening keystrokes of a query do not flash an
 * empty sheet open.
 */
function useRefreshableSheet(hasSuggestions: boolean, isRefreshing: boolean): boolean {
  const [hasOpened, setHasOpened] = useState(false);

  useEffect(() => {
    if (hasSuggestions) {
      setHasOpened(true);
    } else if (!isRefreshing) {
      setHasOpened(false);
    }
  }, [hasSuggestions, isRefreshing]);

  return hasOpened;
}

/**
 * Remembers how many rows the sheet last actually rendered, so a skeleton stands
 * in at that height rather than resizing the sheet under the cursor.
 *
 * Pass `null` whenever real rows are not on screen — including while a skeleton
 * is showing, so results that land during the populate skeleton's minimum
 * display window do not shrink the skeleton before they replace it.
 */
function useRenderedRowCount(renderedCount: number | null): number {
  const [count, setCount] = useState(DEFAULT_SKELETON_ROWS);

  useEffect(() => {
    if (renderedCount !== null && renderedCount > 0) setCount(renderedCount);
  }, [renderedCount]);

  return count;
}

interface SuggestionSheetStateOptions {
  /** Real suggestions are available and pass the host's own relevance checks */
  hasSuggestions: boolean;
  /** How many suggestions those are — used to size the skeleton that replaces them */
  suggestionCount: number;
  /** A request for the current input is in flight or imminent (see useChatSuggestions) */
  isSuggestionsRefreshing: boolean;
  /** Raw mutation pending flag, used to close the optimistic populate skeleton */
  isSuggestionsPending: boolean;
  /** Set when a verb starter has just populated the input */
  forceSearchText: string | null | undefined;
  /** Current (trimmed) editor text */
  searchText: string;
}

interface SuggestionSheetState {
  /** The sheet has something to show — the caller still gates on focus and rival menus */
  hasContent: boolean;
  /** Render skeleton rows instead of suggestions */
  showSkeletonRows: boolean;
  /** How many skeleton rows to render */
  skeletonRowCount: number;
}

export function useSuggestionSheetState({
  hasSuggestions,
  suggestionCount,
  isSuggestionsRefreshing,
  isSuggestionsPending,
  forceSearchText,
  searchText,
}: SuggestionSheetStateOptions): SuggestionSheetState {
  const showPopulateSkeleton = usePopulateSkeletonState(
    forceSearchText,
    isSuggestionsPending,
    searchText
  );
  const hasOpened = useRefreshableSheet(hasSuggestions, isSuggestionsRefreshing);

  const showSkeletonRows = showPopulateSkeleton || (hasOpened && isSuggestionsRefreshing);
  const skeletonRowCount = useRenderedRowCount(
    hasSuggestions && !showSkeletonRows ? suggestionCount : null
  );

  return {
    hasContent: hasSuggestions || showSkeletonRows,
    showSkeletonRows,
    skeletonRowCount,
  };
}

/**
 * Skeleton placeholder rows that visually match real SuggestionItems.
 * Uses staggered fade-in to feel lively rather than a static block.
 */
export const SuggestionSkeletonRows = ({
  count,
  largeText = false,
}: {
  count: number;
  /** Matches the row box to the type size the real rows will land at */
  largeText?: boolean;
}) => (
  <>
    {Array.from({ length: count }, (_, index) => (
      <m.div
        key={index}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2, delay: index * 0.06, ease: [0.25, 0.46, 0.45, 0.94] }}
        className={cn(
          'flex w-full items-center rounded-xl px-4 py-3',
          largeText ? 'min-h-12' : 'min-h-11'
        )}
      >
        <div
          className="h-4 animate-pulse rounded-md bg-surface-hover"
          style={{ width: SKELETON_ROW_WIDTHS[index % SKELETON_ROW_WIDTHS.length] }}
        />
      </m.div>
    ))}
  </>
);
